import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchShareById, getAnalyticsEventContext, logAnalyticsEvent } from './Firebase';
import LegalFooter from './LegalFooter';

const SharePreviewPage = () => {
  const { shareId } = useParams();
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shareViewLogged, setShareViewLogged] = useState(false);

  useEffect(() => {
    setShareViewLogged(false);
  }, [shareId]);

  useEffect(() => {
    let cancelled = false;
    const loadShare = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchShareById(shareId);
        if (!cancelled) {
          setShareData(data || null);
        }
      } catch (loadError) {
        if (!cancelled) {
          console.error('Failed to load share data:', loadError);
          setError('Could not load this shared video.');
          setShareData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadShare();
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  const primaryVideoUrl = useMemo(() => {
    if (typeof shareData?.video_url === 'string' && shareData.video_url.trim()) {
      return shareData.video_url.trim();
    }
    const fallbackVideos = Array.isArray(shareData?.output_videos) ? shareData.output_videos : [];
    const firstVideo = fallbackVideos.find((url) => typeof url === 'string' && url.trim());
    return firstVideo || '';
  }, [shareData]);

  const petName = typeof shareData?.pet_name === 'string' ? shareData.pet_name.trim() : '';
  const petType = String(shareData?.pet_type || '').toLowerCase() === 'cat' ? 'kitten' : 'puppy';

  const trackSharePreviewEvent = useCallback((eventName, properties = {}) => {
    logAnalyticsEvent({
      analyticsContext: getAnalyticsEventContext(),
      eventName,
      properties: {
        screen: 'share_preview',
        share_id: shareId,
        ...properties,
      },
    });
  }, [shareId]);

  useEffect(() => {
    if (loading || shareViewLogged) {
      return;
    }

    trackSharePreviewEvent('fluffball-share-preview-viewed', {
      has_error: Boolean(error),
      has_video: Boolean(primaryVideoUrl),
      pet_type: petType,
    });
    setShareViewLogged(true);
  }, [error, loading, petType, primaryVideoUrl, shareViewLogged, trackSharePreviewEvent]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <div className="flex-1 flex items-center justify-center">Loading shared video...</div>
        <LegalFooter variant="dark" />
      </div>
    );
  }

  if (error || !primaryVideoUrl) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <p className="text-lg mb-4">{error || 'This shared video is unavailable.'}</p>
          <a
            href="https://www.fluffball.app"
            onClick={() => {
              trackSharePreviewEvent('fluffball-share-preview-open-app-clicked', {
                state: 'error',
              });
            }}
            className="px-5 py-3 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-semibold"
          >
            Go to Fluffball.app
          </a>
        </div>
        <LegalFooter variant="dark" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="rounded-2xl overflow-hidden border border-zinc-800 shadow-2xl">
            <video
              src={primaryVideoUrl}
              poster={shareData?.thumbnail_url || ''}
              autoPlay
              loop
              muted
              playsInline
              controls
              className="w-full aspect-[9/16] object-cover bg-black"
            />
          </div>
          <div className="mt-4 text-center">
            <p className="text-base text-zinc-200 mb-3">
              {petName ? `${petName} as a ${petType}` : `A super cute ${petType} video`}
            </p>
            <a
              href="https://www.fluffball.app"
              onClick={() => {
                trackSharePreviewEvent('fluffball-share-preview-open-app-clicked', {
                  pet_type: petType,
                });
              }}
              className="inline-flex items-center justify-center w-full px-5 py-3 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold"
            >
              See your pet as a baby on Fluffball.app
            </a>
          </div>
        </div>
      </div>
      <LegalFooter variant="dark" />
    </div>
  );
};

export default SharePreviewPage;
